import { useState, useEffect, useMemo, useCallback, useRef, type ReactNode } from 'react';
import aviso, { textoDoErro } from '../lib/aviso';
import { LuCoins, LuTarget, LuPhone, LuCopy, LuSparkles, LuUsers, LuUser, LuCheck } from 'react-icons/lu';
import PageHeader, { type PageTab } from '../components/PageHeader';
import HistoricoDaTela from '../components/historico/HistoricoDaTela';
import MonthPicker from '../components/MonthPicker';
import Select from '../components/Select';
import '../components/ListKit.css';
import './GoalsPage.css';
import MetaGoalService, { type MetaGoal } from '../services/MetaGoalService';
import EquipeService, { type Equipe } from '../services/EquipeService';
import TipoUsuarioService from '../services/TipoUsuarioService';
import { apiFetch } from '../services/api';

/**
 * Configurações › Vendas › Metas — proposta de 2026-09-18
 * (docs/designsystem/propostas/metas.html), direção "um número, o time inteiro".
 *
 * A meta deixou de ser um cadastro e virou a tabela do mês: o formulário é a
 * linha, e o campo aparece desenhado em toda linha para a porta ser visível.
 * Três decisões dele governam o que está aqui:
 *
 *  1. As três métricas ficam, como ABAS — uma tabela por vez. Três colunas
 *     vezes (meta + realizado + progresso) dariam nove colunas.
 *  2. A meta da equipe é a SOMA dos membros: a linha do grupo é leitura,
 *     digitar nela distribui igualmente, digitar na pessoa soma para cima.
 *  3. Entra na lista quem o perfil de acesso deixa receber lead — a mesma
 *     régua do `UsuariosVendedores`, que monta a aba Individual.
 *
 * ⚠️ A soma NÃO pode ser só cálculo de tela: o `RelatorioService.
 * PerformanceEquipes` (aba Equipes do monitoramento) lê a meta de equipe do
 * banco. Por isso `sincronizarEquipes` regrava o registro `team` toda vez que
 * a soma muda — sem perguntar nada, que era a modal de confirmação antiga.
 */

const API_URL = import.meta.env.VITE_API_URL || '';

type Metrica = 'value' | 'quantity' | 'calls';

interface DefMetrica {
    id: Metrica;
    label: string;
    icon: ReactNode;
    moeda: boolean;
    subtitulo: string;
    /** Rótulo do campo no cartão de primeira vez. */
    perguntaTime: string;
    unidadePlural: string;
}

const METRICAS: DefMetrica[] = [
    {
        id: 'value',
        label: 'Faturamento',
        icon: <LuCoins />,
        moeda: true,
        subtitulo: 'Quanto cada pessoa precisa vender no mês. O realizado vem das oportunidades marcadas como venda fechada.',
        perguntaTime: 'Meta de faturamento do time',
        unidadePlural: 'em vendas',
    },
    {
        id: 'quantity',
        label: 'Qtd. de vendas',
        icon: <LuTarget />,
        moeda: false,
        subtitulo: 'Quantas vendas cada pessoa precisa fechar no mês. O realizado conta as oportunidades marcadas como venda fechada.',
        perguntaTime: 'Meta de vendas do time',
        unidadePlural: 'vendas',
    },
    {
        id: 'calls',
        label: 'Ligações',
        icon: <LuPhone />,
        moeda: false,
        subtitulo: 'Quantas ligações cada pessoa precisa fazer no mês. O realizado vem das ligações registradas no sistema.',
        perguntaTime: 'Meta de ligações do time',
        unidadePlural: 'ligações',
    },
];

interface Pessoa {
    id: number;
    nome: string;
    equipeId: number | null;
    equipeNome: string | null;
}

interface Grupo {
    chave: string;
    equipeId: number | null;
    nome: string;
    pessoas: Pessoa[];
}

const mesCorrente = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const mesAnteriorDe = (mes: string) => {
    const [ano, m] = mes.split('-').map(Number);
    const d = new Date(ano, m - 2, 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const nomeDoMes = (mes: string) => {
    const [ano, m] = mes.split('-').map(Number);
    return new Date(ano, m - 1, 1).toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
};

/** "setembro" → "Setembro", para começo de frase. */
const maiuscula = (texto: string) => texto.charAt(0).toUpperCase() + texto.slice(1);

const nomeCurtoDoMes = (mes: string) => {
    const [ano, m] = mes.split('-').map(Number);
    return new Date(ano, m - 1, 1).toLocaleDateString('pt-BR', { month: 'long' });
};

/** O tipo já foi gravado em português em versões antigas da API. */
const normalizarTipo = (bruto: string): Metrica => {
    const t = String(bruto).toLowerCase();
    if (t === 'valor' || t === 'value') return 'value';
    if (t === 'vendas' || t === 'quantity') return 'quantity';
    return 'calls';
};

const formatar = (valor: number, moeda: boolean) =>
    moeda
        ? valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
        : valor.toLocaleString('pt-BR');

/** Como o número vai para DENTRO do campo em edição: cru, sem R$ nem milhar. */
const paraEdicao = (valor: number, moeda: boolean) =>
    moeda ? String(valor).replace('.', ',') : String(valor);

/**
 * Tolerante de propósito: "14000", "14.000", "R$ 14.000,50" e "14000,5" são a
 * mesma coisa para quem está preenchendo uma tabela inteira.
 * Devolve null quando o campo ficou vazio (ou zero) — que é o gesto de apagar.
 */
const interpretar = (texto: string, moeda: boolean): number | null => {
    const limpo = texto.replace(/[^\d,.-]/g, '').trim();
    if (!limpo) return null;
    const normal = limpo.replace(/\./g, '').replace(',', '.');
    const n = Number(normal);
    if (!Number.isFinite(n) || n <= 0) return null;
    return moeda ? Math.round(n * 100) / 100 : Math.round(n);
};

const percentual = (realizado: number, meta: number) =>
    meta > 0 ? Math.round((realizado / meta) * 100) : null;

/** Barra de progresso do bullet — o mesmo desenho do Analytics. */
const Progresso = ({ realizado, meta }: { realizado: number; meta: number }) => {
    const pct = percentual(realizado, meta);
    if (pct === null) return <span className="mta-progress"><span className="mta-track" /><span className="mta-pct">—</span></span>;
    return (
        <span className="mta-progress">
            <span className="mta-track">
                <i className={pct >= 100 ? 'is-done' : ''} style={{ width: `${Math.min(100, pct)}%` }} />
            </span>
            <span className="mta-pct">{pct}%</span>
        </span>
    );
};

const iniciais = (nome: string) =>
    nome.trim().split(/\s+/).slice(0, 2).map(p => p[0]?.toUpperCase() ?? '').join('');

/* As colunas vêm de um token da casca para a folha poder trocá-las em tela
   estreita — com o grid escrito no `style` de cada linha, o media query não
   teria como alcançar, e a coluna de progresso ficava cortada a 900px. */
const COLUNAS = 'var(--mta-cols)';

type Escopo = string; // 'todos' | 'sem-meta' | `team-${id}` | 'sem-equipe'

const GoalsPage = () => {
    const [metrica, setMetrica] = useState<Metrica>('value');
    const [mes, setMes] = useState(mesCorrente());

    const [pessoas, setPessoas] = useState<Pessoa[]>([]);
    const [equipes, setEquipes] = useState<Equipe[]>([]);
    const [metas, setMetas] = useState<MetaGoal[]>([]);
    const [totalMesAnterior, setTotalMesAnterior] = useState(0);

    const [carregandoBase, setCarregandoBase] = useState(true);
    const [carregandoMes, setCarregandoMes] = useState(true);
    const [ocupado, setOcupado] = useState(false);

    /** Texto em edição, por linha. Some assim que a linha é salva. */
    const [rascunhos, setRascunhos] = useState<Record<string, string>>({});

    const [popAberto, setPopAberto] = useState(false);
    const [popValor, setPopValor] = useState('');
    const [popEscopo, setPopEscopo] = useState<Escopo>('todos');
    const [popSubstituir, setPopSubstituir] = useState(false);
    const popRef = useRef<HTMLDivElement>(null);

    const [primeiroValor, setPrimeiroValor] = useState('');
    const [primeiroDividir, setPrimeiroDividir] = useState(true);

    const def = METRICAS.find(m => m.id === metrica)!;
    const mesAnterior = mesAnteriorDe(mes);

    /* ─── carga ──────────────────────────────────────────────── */

    const carregarBase = useCallback(async () => {
        setCarregandoBase(true);
        try {
            const [eqs, usuariosRes, tipos] = await Promise.all([
                EquipeService.buscarTodas(),
                apiFetch(`${API_URL}/api/Usuario/BuscarTodos`),
                TipoUsuarioService.buscarTodos(),
            ]);

            const usuarios = await usuariosRes.json();
            const lista: any[] = usuarios?.sucesso && Array.isArray(usuarios.dados) ? usuarios.dados : [];

            /* Decisão 3: entra quem o PERFIL deixa receber lead. Papel não serve
               — nesta base os administradores é que vendem, e perfil criado pela
               empresa nem tem código. */
            const podeVender = new Map(tipos.map(t => [t.id, t.canReceiveLead]));
            const equipeDe = new Map<number, Equipe>();
            eqs.forEach(e => e.membros.forEach(m => equipeDe.set(m.id, e)));

            const vendedores: Pessoa[] = lista
                .filter(u => !u.userDeleted && podeVender.get(u.userTypeuserid) === true)
                .map(u => {
                    const eq = equipeDe.get(u.userId) ?? null;
                    return {
                        id: u.userId,
                        nome: u.userName,
                        equipeId: eq?.id ?? null,
                        equipeNome: eq?.nome ?? null,
                    };
                })
                .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'));

            setEquipes(eqs);
            setPessoas(vendedores);
        } catch {
            aviso.erro('Não foi possível carregar as pessoas e equipes.');
        } finally {
            setCarregandoBase(false);
        }
    }, []);

    const carregarMes = useCallback(async (alvo: string) => {
        setCarregandoMes(true);
        try {
            const [doMes, doAnterior] = await Promise.all([
                MetaGoalService.buscarTodas(alvo),
                MetaGoalService.buscarTodas(mesAnteriorDe(alvo)).catch(() => [] as MetaGoal[]),
            ]);
            setMetas(doMes);
            /* Só metas de pessoa: a da equipe é derivada da soma, contá-la no
               aviso faria "5 metas" onde há 4 pessoas com meta. */
            setTotalMesAnterior(doAnterior.filter(m => m.tipoAlvo === 'user').length);
            setRascunhos({});
        } catch (err: any) {
            aviso.erro(textoDoErro(err, 'Não foi possível carregar as metas do mês.'));
        } finally {
            setCarregandoMes(false);
        }
    }, []);

    useEffect(() => { carregarBase(); }, [carregarBase]);
    useEffect(() => { carregarMes(mes); }, [mes, carregarMes]);

    useEffect(() => {
        if (!popAberto) return;
        const fora = (e: MouseEvent) => {
            if (popRef.current && !popRef.current.contains(e.target as Node)) setPopAberto(false);
        };
        document.addEventListener('mousedown', fora);
        return () => document.removeEventListener('mousedown', fora);
    }, [popAberto]);

    /* ─── leitura ────────────────────────────────────────────── */

    const metaDe = useCallback(
        (tipoAlvo: 'user' | 'team', alvoId: number, qual: Metrica = metrica) =>
            metas.find(m => m.tipoAlvo === tipoAlvo && m.alvoId === alvoId && normalizarTipo(m.tipo) === qual),
        [metas, metrica],
    );

    const grupos = useMemo<Grupo[]>(() => {
        const porEquipe = new Map<number, Pessoa[]>();
        const soltas: Pessoa[] = [];

        pessoas.forEach(p => {
            if (p.equipeId === null) { soltas.push(p); return; }
            const atual = porEquipe.get(p.equipeId) ?? [];
            atual.push(p);
            porEquipe.set(p.equipeId, atual);
        });

        const lista: Grupo[] = equipes
            .filter(e => (porEquipe.get(e.id)?.length ?? 0) > 0)
            .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'))
            .map(e => ({
                chave: `team-${e.id}`,
                equipeId: e.id,
                nome: e.nome,
                pessoas: porEquipe.get(e.id)!,
            }));

        if (soltas.length > 0) {
            lista.push({ chave: 'sem-equipe', equipeId: null, nome: 'Sem equipe', pessoas: soltas });
        }
        return lista;
    }, [pessoas, equipes]);

    const somaDoGrupo = useCallback(
        (g: Grupo) => g.pessoas.reduce((t, p) => t + (metaDe('user', p.id)?.valorAlvo ?? 0), 0),
        [metaDe],
    );

    const realizadoDoGrupo = useCallback(
        (g: Grupo) => g.pessoas.reduce((t, p) => t + (metaDe('user', p.id)?.valorRealizado ?? 0), 0),
        [metaDe],
    );

    const metasDaMetrica = useMemo(
        () => metas.filter(m => m.tipoAlvo === 'user' && normalizarTipo(m.tipo) === metrica),
        [metas, metrica],
    );

    const totalMeta = metasDaMetrica.reduce((t, m) => t + m.valorAlvo, 0);
    const totalRealizado = metasDaMetrica.reduce((t, m) => t + m.valorRealizado, 0);

    const contagemPorMetrica = useMemo(() => {
        const conta = (qual: Metrica) =>
            metas.filter(m => m.tipoAlvo === 'user' && normalizarTipo(m.tipo) === qual).length;
        return { value: conta('value'), quantity: conta('quantity'), calls: conta('calls') };
    }, [metas]);

    /* ─── escrita ────────────────────────────────────────────── */

    /**
     * Decisão 2: depois de qualquer mudança nas pessoas, o registro `team` de
     * cada equipe vira a soma dos seus membros. Não é enfeite de tela — a aba
     * Equipes do monitoramento lê esse registro do banco.
     */
    const sincronizarEquipes = async (metasAtuais: MetaGoal[]) => {
        const valorDe = (tipoAlvo: 'user' | 'team', id: number) =>
            metasAtuais.find(m => m.tipoAlvo === tipoAlvo && m.alvoId === id && normalizarTipo(m.tipo) === metrica);

        const trabalhos = grupos
            .filter(g => g.equipeId !== null)
            .map(async g => {
                const soma = g.pessoas.reduce((t, p) => t + (valorDe('user', p.id)?.valorAlvo ?? 0), 0);
                const atual = valorDe('team', g.equipeId!);

                if (soma <= 0) {
                    if (atual) await MetaGoalService.excluir(atual.id);
                    return;
                }
                if (!atual) {
                    await MetaGoalService.criar({ tipoAlvo: 'team', alvoId: g.equipeId!, tipo: metrica, valorAlvo: soma, mes });
                    return;
                }
                if (atual.valorAlvo !== soma) {
                    await MetaGoalService.atualizar(atual.id, {
                        tipoAlvo: 'team', alvoId: g.equipeId!, tipo: metrica, valorAlvo: soma, mes,
                    });
                }
            });

        await Promise.all(trabalhos);
    };

    /** Uma pessoa, um valor. `null` apaga a meta daquele mês. */
    const gravarPessoa = async (pessoaId: number, valor: number | null, metasAtuais: MetaGoal[]) => {
        const atual = metasAtuais.find(
            m => m.tipoAlvo === 'user' && m.alvoId === pessoaId && normalizarTipo(m.tipo) === metrica,
        );

        if (valor === null) {
            if (atual) await MetaGoalService.excluir(atual.id);
            return;
        }
        if (!atual) {
            await MetaGoalService.criar({ tipoAlvo: 'user', alvoId: pessoaId, tipo: metrica, valorAlvo: valor, mes });
            return;
        }
        if (atual.valorAlvo !== valor) {
            await MetaGoalService.atualizar(atual.id, {
                tipoAlvo: 'user', alvoId: pessoaId, tipo: metrica, valorAlvo: valor, mes,
            });
        }
    };

    /**
     * Todo caminho de escrita passa por aqui.
     *
     * A tabela muda ANTES da rede: gravar uma célula custa até três idas à API
     * (a meta, a releitura, a soma da equipe), e quem está preenchendo o time
     * inteiro com Tab não pode esperar isso por linha — medido em 2026-09-18,
     * a linha ficava três segundos mostrando o valor velho na soma. O servidor
     * responde depois e reconcilia; se falhar, recarrega e avisa.
     */
    const aplicar = async (
        mudancas: { pessoaId: number; valor: number | null }[],
        mensagem?: string,
    ) => {
        if (mudancas.length === 0) return;

        const antes = metas;
        setMetas(prev => {
            const novas = [...prev];
            mudancas.forEach(({ pessoaId, valor }) => {
                const i = novas.findIndex(
                    m => m.tipoAlvo === 'user' && m.alvoId === pessoaId && normalizarTipo(m.tipo) === metrica,
                );
                if (valor === null) {
                    if (i >= 0) novas.splice(i, 1);
                } else if (i >= 0) {
                    novas[i] = { ...novas[i], valorAlvo: valor };
                } else {
                    novas.push({
                        id: -(pessoaId + 1), // provisório: some na primeira releitura
                        tipoAlvo: 'user',
                        alvoId: pessoaId,
                        alvoNome: '',
                        tipo: metrica,
                        valorAlvo: valor,
                        valorRealizado: 0,
                        mes,
                    });
                }
            });
            return novas;
        });
        setRascunhos({});
        setOcupado(true);

        try {
            await Promise.all(mudancas.map(m => gravarPessoa(m.pessoaId, m.valor, antes)));
            const recarregadas = await MetaGoalService.buscarTodas(mes);
            setMetas(recarregadas);
            await sincronizarEquipes(recarregadas);
            if (mensagem) aviso.ok(mensagem);
        } catch (err: any) {
            aviso.erro(textoDoErro(err, 'Não foi possível salvar.'));
            setMetas(await MetaGoalService.buscarTodas(mes).catch(() => antes));
        } finally {
            setOcupado(false);
        }
    };

    const confirmarPessoa = async (pessoa: Pessoa) => {
        const chave = `u-${pessoa.id}`;
        const texto = rascunhos[chave];
        if (texto === undefined) return;

        const valor = interpretar(texto, def.moeda);
        const atual = metaDe('user', pessoa.id);

        if ((atual?.valorAlvo ?? null) === valor) {
            setRascunhos(r => { const c = { ...r }; delete c[chave]; return c; });
            return;
        }
        /* Sem toast: quem digitou vê o número formatado na linha e a soma da
           equipe mudar junto — o aviso seria barulho a cada célula. O toast
           fica para as operações em massa e para o erro. */
        await aplicar([{ pessoaId: pessoa.id, valor }]);
    };

    /** Digitar no total do grupo distribui igualmente — o sentido de cima para baixo. */
    const confirmarGrupo = async (g: Grupo) => {
        const chave = `g-${g.chave}`;
        const texto = rascunhos[chave];
        if (texto === undefined) return;

        const total = interpretar(texto, def.moeda);
        if (total === null) {
            await aplicar(g.pessoas.map(p => ({ pessoaId: p.id, valor: null })), `Metas de ${g.nome} removidas.`);
            return;
        }

        const n = g.pessoas.length;
        const base = def.moeda ? Math.floor((total * 100) / n) / 100 : Math.floor(total / n);
        const resto = Math.round((total - base * n) * 100) / 100;

        await aplicar(
            g.pessoas.map((p, i) => ({ pessoaId: p.id, valor: i === 0 ? Math.round((base + resto) * 100) / 100 : base })),
            `${formatar(total, def.moeda)} dividido entre ${n} ${n === 1 ? 'pessoa' : 'pessoas'}.`,
        );
    };

    const alvosDoEscopo = (escopo: Escopo): Pessoa[] => {
        if (escopo === 'todos') return pessoas;
        if (escopo === 'sem-meta') return pessoas.filter(p => !metaDe('user', p.id));
        if (escopo === 'sem-equipe') return pessoas.filter(p => p.equipeId === null);
        const id = Number(escopo.replace('team-', ''));
        return pessoas.filter(p => p.equipeId === id);
    };

    const alvosDaAplicacao = useMemo(() => {
        const base = alvosDoEscopo(popEscopo);
        return popSubstituir ? base : base.filter(p => !metaDe('user', p.id));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [popEscopo, popSubstituir, pessoas, metas, metrica]);

    const aplicarAVarios = async () => {
        const valor = interpretar(popValor, def.moeda);
        if (valor === null) { aviso.atencao('Informe um valor maior que zero.'); return; }
        if (alvosDaAplicacao.length === 0) { aviso.atencao('Ninguém neste escopo para receber a meta.'); return; }

        setPopAberto(false);
        await aplicar(
            alvosDaAplicacao.map(p => ({ pessoaId: p.id, valor })),
            `${formatar(valor, def.moeda)} aplicado a ${alvosDaAplicacao.length} ${alvosDaAplicacao.length === 1 ? 'pessoa' : 'pessoas'}.`,
        );
        setPopValor('');
    };

    const definirPrimeiraVez = async () => {
        const total = interpretar(primeiroValor, def.moeda);
        if (total === null) { aviso.atencao('Informe um valor maior que zero.'); return; }
        if (pessoas.length === 0) return;

        if (!primeiroDividir) {
            await aplicar(
                pessoas.map(p => ({ pessoaId: p.id, valor: total })),
                `Meta definida para ${pessoas.length} ${pessoas.length === 1 ? 'pessoa' : 'pessoas'}.`,
            );
            setPrimeiroValor('');
            return;
        }

        const n = pessoas.length;
        const base = def.moeda ? Math.floor((total * 100) / n) / 100 : Math.floor(total / n);
        const resto = Math.round((total - base * n) * 100) / 100;
        await aplicar(
            pessoas.map((p, i) => ({ pessoaId: p.id, valor: i === 0 ? Math.round((base + resto) * 100) / 100 : base })),
            `${formatar(total, def.moeda)} dividido entre ${n} ${n === 1 ? 'pessoa' : 'pessoas'}.`,
        );
        setPrimeiroValor('');
    };

    const copiarMesAnterior = async () => {
        setOcupado(true);
        try {
            const r = await MetaGoalService.replicar({ mesOrigem: mesAnterior, mesDestino: mes });
            if (r.copiadas === 0) {
                aviso.info(`Nada a copiar: as metas de ${nomeCurtoDoMes(mesAnterior)} já existem neste mês.`);
            } else {
                aviso.ok(
                    `${r.copiadas} ${r.copiadas === 1 ? 'meta copiada' : 'metas copiadas'} de ${nomeCurtoDoMes(mesAnterior)}`
                    + (r.ignoradas > 0 ? ` · ${r.ignoradas} já existiam.` : '.'),
                );
                await carregarMes(mes);
            }
        } catch (err: any) {
            aviso.erro(textoDoErro(err, 'Não foi possível copiar as metas.'));
        } finally {
            setOcupado(false);
        }
    };

    /* ─── render ─────────────────────────────────────────────── */

    const abas: PageTab[] = METRICAS.map(m => ({
        id: m.id,
        label: m.label,
        icon: m.icon,
        count: contagemPorMetrica[m.id],
    }));

    const carregando = carregandoBase || carregandoMes;
    const semMetasNoMes = !carregando && metasDaMetrica.length === 0;
    const podeCopiar = totalMesAnterior > 0;
    const comMeta = metasDaMetrica.length;

    const campo = (
        chave: string,
        valorAtual: number | undefined,
        aoConfirmar: () => void,
        rotulo: string,
    ) => (
        <input
            type="text"
            inputMode="decimal"
            className="mta-input"
            aria-label={rotulo}
            placeholder="—"
            value={rascunhos[chave] ?? (valorAtual !== undefined ? formatar(valorAtual, def.moeda) : '')}
            onFocus={() => setRascunhos(r => ({
                ...r,
                [chave]: r[chave] ?? (valorAtual !== undefined ? paraEdicao(valorAtual, def.moeda) : ''),
            }))}
            onChange={e => setRascunhos(r => ({ ...r, [chave]: e.target.value }))}
            onBlur={aoConfirmar}
            onKeyDown={e => {
                if (e.key === 'Enter') { e.currentTarget.blur(); }
                if (e.key === 'Escape') {
                    setRascunhos(r => { const c = { ...r }; delete c[chave]; return c; });
                    e.currentTarget.blur();
                }
            }}
        />
    );

    return (
        <div className="page-shell lk-screen mta-screen">
            <PageHeader
                title="Metas"
                subtitle={def.subtitulo}
                actions={
                    <div className="lk-actions">
                        <MonthPicker value={mes} onChange={setMes} />
                        {podeCopiar && (
                            <button type="button" className="lk-btn" disabled={ocupado} onClick={copiarMesAnterior}>
                                <LuCopy size={14} /> Copiar de {nomeCurtoDoMes(mesAnterior)}
                            </button>
                        )}
                        {/* Histórico (fase 5): metas não têm ficha, o histórico é da tela. */}
                        <HistoricoDaTela entidade="meta" titulo="Metas" />
                    </div>
                }
                tabs={abas}
                activeTab={metrica}
                onTabChange={id => setMetrica(id as Metrica)}
            />

            <div className="page-body lk-fill">
                {!carregando && comMeta > 0 && (
                    <div className="mta-summary">
                        <span className="mta-summary-label">Meta do time</span>
                        <span className="mta-summary-value">{formatar(totalMeta, def.moeda)}</span>
                        <span className="mta-summary-sep" />
                        <span className="mta-summary-label">Realizado até hoje</span>
                        <span className="mta-summary-value">{formatar(totalRealizado, def.moeda)}</span>
                        <span className="mta-summary-sep" />
                        <span className="mta-summary-bar">
                            <Progresso realizado={totalRealizado} meta={totalMeta} />
                        </span>
                    </div>
                )}

                {!carregando && pessoas.length > 0 && (
                    <div className="mta-bar">
                        <div className="mta-apply" ref={popRef}>
                            <button
                                type="button"
                                className="lk-btn"
                                disabled={ocupado}
                                onClick={() => setPopAberto(v => !v)}
                            >
                                <LuSparkles size={14} /> Aplicar valor a vários
                            </button>

                            {popAberto && (
                                <div className="mta-pop" role="dialog" aria-label="Aplicar valor a vários">
                                    <div className="mta-pop-title">Aplicar um valor a vários</div>

                                    <label className="mta-pop-label" htmlFor="mta-pop-valor">Valor</label>
                                    <input
                                        id="mta-pop-valor"
                                        className="mta-pop-input"
                                        type="text"
                                        inputMode="decimal"
                                        autoFocus
                                        placeholder={def.moeda ? 'R$ 0,00' : '0'}
                                        value={popValor}
                                        onChange={e => setPopValor(e.target.value)}
                                        onKeyDown={e => { if (e.key === 'Enter') aplicarAVarios(); }}
                                    />

                                    <span className="mta-pop-label">Para quem</span>
                                    <Select
                                        size="sm"
                                        value={popEscopo}
                                        onChange={setPopEscopo}
                                        options={[
                                            { value: 'todos', label: 'Todas as pessoas' },
                                            { value: 'sem-meta', label: 'Quem ainda não tem meta' },
                                            ...grupos.filter(g => g.equipeId !== null).map(g => ({ value: g.chave, label: g.nome })),
                                            ...(grupos.some(g => g.equipeId === null)
                                                ? [{ value: 'sem-equipe', label: 'Sem equipe' }]
                                                : []),
                                        ]}
                                    />

                                    <label className="mta-pop-check">
                                        <input
                                            type="checkbox"
                                            checked={popSubstituir}
                                            onChange={e => setPopSubstituir(e.target.checked)}
                                        />
                                        <span>Substituir quem já tem meta</span>
                                    </label>

                                    {alvosDaAplicacao.length === 0 && (
                                        <p className="mta-pop-aviso">
                                            Todo mundo desse escopo já tem meta — marque “substituir” para trocar
                                            o valor de quem já tem.
                                        </p>
                                    )}

                                    <div className="mta-pop-foot">
                                        <button
                                            type="button"
                                            className="lk-btn is-primary"
                                            disabled={alvosDaAplicacao.length === 0}
                                            onClick={aplicarAVarios}
                                        >
                                            <LuCheck size={14} />
                                            Aplicar a {alvosDaAplicacao.length} {alvosDaAplicacao.length === 1 ? 'pessoa' : 'pessoas'}
                                        </button>
                                        <button type="button" className="lk-btn" onClick={() => setPopAberto(false)}>
                                            Cancelar
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                        <span className="mta-hint">
                            Clique no valor para editar · <kbd>Tab</kbd> vai para a próxima pessoa
                        </span>
                        <span className="lk-count">
                            {pessoas.length} {pessoas.length === 1 ? 'pessoa' : 'pessoas'} · {comMeta} com meta em {nomeCurtoDoMes(mes)}
                        </span>
                    </div>
                )}

                {semMetasNoMes && podeCopiar && (
                    <div className="mta-banner">
                        <LuCopy size={18} />
                        <span className="mta-banner-text">
                            <b>{maiuscula(nomeDoMes(mes))} ainda está vazio.</b>{' '}
                            {maiuscula(nomeCurtoDoMes(mesAnterior))} tem {totalMesAnterior}{' '}
                            {totalMesAnterior === 1 ? 'meta' : 'metas'} — dá para trazer todas e ajustar só o que mudou.
                        </span>
                        <button type="button" className="lk-btn is-primary" disabled={ocupado} onClick={copiarMesAnterior}>
                            Copiar as metas de {nomeCurtoDoMes(mesAnterior)}
                        </button>
                    </div>
                )}

                {!carregando && pessoas.length === 0 && (
                    <div className="lk-blank">
                        <div className="lk-blank-title">Ninguém para receber meta</div>
                        <p className="lk-blank-text">
                            A lista traz quem o perfil de acesso deixa receber lead — é a mesma régua da aba
                            Individual. Libere “pode receber lead” no perfil de quem vende, em Perfis de acesso.
                        </p>
                    </div>
                )}

                {semMetasNoMes && !podeCopiar && pessoas.length > 0 && (
                    <div className="mta-first">
                        <div className="mta-first-card">
                            <div className="mta-first-title">
                                {maiuscula(nomeDoMes(mes))} ainda não tem metas
                            </div>
                            <p className="mta-first-text">
                                Comece por um número só: o quanto o time inteiro precisa entregar no mês.
                                Depois dá para ajustar pessoa por pessoa na tabela.
                            </p>

                            <label className="mta-first-label" htmlFor="mta-primeiro">
                                {def.perguntaTime} em {nomeCurtoDoMes(mes)}
                            </label>
                            <div className="mta-first-field">
                                {def.moeda && <span className="mta-first-prefix">R$</span>}
                                <input
                                    id="mta-primeiro"
                                    type="text"
                                    inputMode="decimal"
                                    autoComplete="off"
                                    value={primeiroValor}
                                    onChange={e => setPrimeiroValor(e.target.value)}
                                    onKeyDown={e => { if (e.key === 'Enter') definirPrimeiraVez(); }}
                                />
                            </div>

                            <label className={`mta-option ${primeiroDividir ? 'is-on' : ''}`}>
                                <input
                                    type="radio"
                                    name="mta-divisao"
                                    checked={primeiroDividir}
                                    onChange={() => setPrimeiroDividir(true)}
                                />
                                <span>
                                    <b>Dividir igualmente entre as {pessoas.length} pessoas</b>
                                    <span className="mta-option-hint">
                                        {interpretar(primeiroValor, def.moeda)
                                            ? `${formatar(
                                                def.moeda
                                                    ? Math.round((interpretar(primeiroValor, def.moeda)! / pessoas.length) * 100) / 100
                                                    : Math.floor(interpretar(primeiroValor, def.moeda)! / pessoas.length),
                                                def.moeda,
                                            )} para cada uma`
                                            : 'Cada pessoa recebe a mesma fatia do total.'}
                                    </span>
                                </span>
                            </label>

                            <label className={`mta-option ${!primeiroDividir ? 'is-on' : ''}`}>
                                <input
                                    type="radio"
                                    name="mta-divisao"
                                    checked={!primeiroDividir}
                                    onChange={() => setPrimeiroDividir(false)}
                                />
                                <span>
                                    <b>Mesmo valor para cada pessoa</b>
                                    <span className="mta-option-hint">
                                        {interpretar(primeiroValor, def.moeda)
                                            ? `O time inteiro somaria ${formatar(interpretar(primeiroValor, def.moeda)! * pessoas.length, def.moeda)}.`
                                            : 'O valor digitado vale para cada pessoa, não para o time.'}
                                    </span>
                                </span>
                            </label>

                            <div className="mta-first-foot">
                                <button type="button" className="lk-btn is-primary" disabled={ocupado} onClick={definirPrimeiraVez}>
                                    <LuCheck size={14} /> Definir metas de {nomeCurtoDoMes(mes)}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {(carregando || !semMetasNoMes || podeCopiar) && pessoas.length > 0 && (
                    <div className="lk-table">
                        <div className="lk-scroll">
                            <div className="lk-row" style={{ gridTemplateColumns: COLUNAS }}>
                                <div className="lk-head">Pessoa</div>
                                <div className="lk-head mta-right">Meta do mês</div>
                                <div className="lk-head mta-right">Realizado</div>
                                <div className="lk-head">Progresso</div>
                            </div>

                            <div className="lk-body">
                                {carregando
                                    ? Array.from({ length: 6 }).map((_, i) => <div key={i} className="lk-skeleton" />)
                                    : grupos.map(g => {
                                        const soma = somaDoGrupo(g);
                                        const realizado = realizadoDoGrupo(g);
                                        return (
                                            <div key={g.chave} className="mta-group">
                                                <div className="lk-row mta-group-row" style={{ gridTemplateColumns: COLUNAS }}>
                                                    <span className="lk-cell mta-group-name">
                                                        {g.equipeId === null ? <LuUser size={14} /> : <LuUsers size={14} />}
                                                        <span>{g.nome}</span>
                                                        <span className="mta-group-count">
                                                            {g.pessoas.length} {g.pessoas.length === 1 ? 'pessoa' : 'pessoas'}
                                                        </span>
                                                    </span>
                                                    <span className="lk-cell mta-right">
                                                        {g.equipeId === null
                                                            ? <span className="mta-readonly">{soma > 0 ? formatar(soma, def.moeda) : '—'}</span>
                                                            : campo(
                                                                `g-${g.chave}`,
                                                                soma > 0 ? soma : undefined,
                                                                () => confirmarGrupo(g),
                                                                `Meta total de ${g.nome}`,
                                                            )}
                                                    </span>
                                                    <span className="lk-cell mta-right mta-num">
                                                        {soma > 0 ? formatar(realizado, def.moeda) : '—'}
                                                    </span>
                                                    <span className="lk-cell">
                                                        <Progresso realizado={realizado} meta={soma} />
                                                    </span>
                                                </div>

                                                {g.pessoas.map(p => {
                                                    const meta = metaDe('user', p.id);
                                                    return (
                                                        <div key={p.id} className="lk-row" style={{ gridTemplateColumns: COLUNAS }}>
                                                            <span className="lk-cell mta-person">
                                                                <span className="mta-avatar" aria-hidden="true">{iniciais(p.nome)}</span>
                                                                <span className="lk-strong">{p.nome}</span>
                                                            </span>
                                                            <span className="lk-cell mta-right">
                                                                {campo(`u-${p.id}`, meta?.valorAlvo, () => confirmarPessoa(p), `Meta de ${p.nome}`)}
                                                            </span>
                                                            <span className="lk-cell mta-right mta-num">
                                                                {meta ? formatar(meta.valorRealizado, def.moeda) : <span className="lk-empty-value">—</span>}
                                                            </span>
                                                            <span className="lk-cell">
                                                                <Progresso realizado={meta?.valorRealizado ?? 0} meta={meta?.valorAlvo ?? 0} />
                                                            </span>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        );
                                    })}

                                {!carregando && comMeta > 0 && (
                                    <div className="lk-row mta-total-row" style={{ gridTemplateColumns: COLUNAS }}>
                                        <span className="lk-cell mta-group-name"><span>Total do time</span></span>
                                        <span className="lk-cell mta-right">
                                            <span className="mta-readonly">{formatar(totalMeta, def.moeda)}</span>
                                        </span>
                                        <span className="lk-cell mta-right mta-num">{formatar(totalRealizado, def.moeda)}</span>
                                        <span className="lk-cell"><Progresso realizado={totalRealizado} meta={totalMeta} /></span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default GoalsPage;
